import { useEffect, useRef } from 'react';

interface BenefitsTestimonialsProps {
  onCTA: () => void;
}

export default function BenefitsTestimonials({ onCTA }: BenefitsTestimonialsProps) {
  const benefitsRef = useRef<HTMLDivElement>(null);
  const testimonialsRef = useRef<HTMLDivElement>(null);
  const urgencyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = [benefitsRef.current, testimonialsRef.current, urgencyRef.current].filter(Boolean) as HTMLDivElement[];
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) entry.target.classList.add('opacity-100', 'translate-y-0'); },
      { threshold: 0.1 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const benefits = [
    {
      icon: 'ri-medal-fill',
      label: '5-Year Written Warranty',
      desc: 'Backed by a formal written guarantee',
      seal: true,
    },
    { icon: 'ri-forbid-fill', label: 'No Demolition', desc: 'Non-destructive process, zero structural damage', seal: false },
    { icon: 'ri-time-fill', label: 'No Downtime', desc: 'Operations continue during treatment', seal: false },
    { icon: 'ri-drop-fill', label: 'Works Under Active Leaks', desc: 'Effective even with water actively flowing', seal: false },
  ];

  const testimonials = [
    {
      quote: 'Other companies required major construction for 4x the cost. Worth every dollar.',
      author: 'J P',
      stars: 5,
    },
    {
      quote: 'Every company had a different explanation. CL Wizard are the only ones who fixed it.',
      author: 'Flying Arrow Productions',
      stars: 5,
    },
    {
      quote: 'The only company to provide a lasting solution.',
      author: 'Joshua Mendel',
      stars: 5,
    },
    {
      quote: 'Excellent service from start to finish. The team was professional, knowledgeable, and delivered exactly what they promised.',
      author: 'Maria S.',
      stars: 5,
    },
  ];

  const urgencyItems = [
    'Persistent leaks despite repairs',
    'Water intrusion under hydrostatic pressure',
    'Escalating costs',
    'Structural concerns',
  ];

  return (
    <>
      {/* Benefits — light background */}
      <section className="bg-[#f4f6f9] py-16 md:py-24">
        <div
          ref={benefitsRef}
          className="max-w-6xl mx-auto px-4 md:px-8 opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          <div className="text-center mb-12">
            <span className="inline-block bg-[#0055ff]/10 border border-[#0055ff]/30 text-[#0055ff] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              Why CLWizard®
            </span>
            <h2 className="text-2xl md:text-4xl font-black text-[#0a0e1a]">Built for Structures That Can&apos;t Afford to Fail</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
            {benefits.map((b) => (
              <div key={b.label} className="bg-white border border-gray-200 rounded-xl p-5 md:p-6 text-center hover:border-[#0055ff]/40 hover:shadow-md transition-all duration-200">
                <span className="w-12 h-12 flex items-center justify-center mx-auto mb-4 bg-[#0055ff]/10 rounded-full text-[#0055ff] text-2xl">
                  <i className={b.icon}></i>
                </span>
                <h3 className="text-[#0a0e1a] font-bold text-sm mb-2">{b.label}</h3>
                <p className="text-gray-500 text-xs leading-relaxed mb-3">{b.desc}</p>
                {b.seal && (
                  <div className="flex justify-center mt-3">
                    <img
                      src="https://clwizard.com/wp-content/uploads/2025/04/Selo_Garantia_Product_and_Service.png"
                      alt="5-Year Warranty Seal"
                      className="h-20 w-20 object-contain"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials — dark background */}
      <section id="reviews" className="bg-[#0a0e1a] py-16 md:py-24">
        <div
          ref={testimonialsRef}
          className="max-w-6xl mx-auto px-4 md:px-8 opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {/* Google Rating Badge */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-4 bg-white/5 border border-white/10 rounded-2xl px-6 sm:px-8 py-5 text-center sm:text-left">
              <div className="flex flex-col items-center">
                <span className="text-white font-black text-xl sm:text-2xl tracking-tight">EXCELLENT</span>
                <div className="flex gap-1 my-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} className="w-5 h-5 flex items-center justify-center text-yellow-400">
                      <i className="ri-star-fill text-lg"></i>
                    </span>
                  ))}
                </div>
                <span className="text-gray-400 text-xs font-medium">Based on 14 Google Reviews</span>
              </div>
              <div className="hidden sm:block w-px h-14 bg-white/10"></div>
              <div className="flex flex-col items-center gap-1">
                <span className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-google-fill text-3xl text-white"></i>
                </span>
                <span className="text-gray-400 text-xs">Google</span>
              </div>
            </div>
          </div>

          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-black text-white">What Our Clients Say</h2>
          </div>

          {/* Google Review Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-10">
            {testimonials.map((t) => (
              <div
                key={t.author}
                className="bg-white/5 border border-white/10 rounded-xl p-5 md:p-6 hover:border-[#0055ff]/30 transition-all duration-200"
              >
                {/* Card header */}
                <div className="flex items-start justify-between mb-4 gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-full bg-[#0055ff]/20 text-[#4d8fff] font-bold text-base">
                      {t.author.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{t.author}</p>
                      <div className="flex gap-0.5 mt-0.5">
                        {Array.from({ length: t.stars }).map((_, i) => (
                          <span key={i} className="w-3 h-3 flex items-center justify-center text-yellow-400">
                            <i className="ri-star-fill text-xs"></i>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-2 py-1 shrink-0">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-google-fill text-xs text-gray-400"></i>
                    </span>
                    <span className="text-gray-500 text-xs font-medium hidden sm:inline">Google Review</span>
                  </div>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
              </div>
            ))}
          </div>

          {/* Grove Club Pull-Quote */}
          <div className="relative bg-[#0055ff]/8 border-l-4 border-[#0055ff] rounded-r-xl p-6 md:p-10 mb-16">
            <span className="absolute top-4 left-6 text-[#0055ff]/20 font-serif text-6xl md:text-8xl leading-none select-none">&ldquo;</span>
            <div className="relative z-10">
              <p className="text-white text-base md:text-xl font-medium leading-relaxed mb-5 italic">
                &ldquo;As a Property Manager for 40 years — after completion, numerous heavy rainstorms, not a single drop of water.&rdquo;
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} className="w-4 h-4 flex items-center justify-center text-yellow-400">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-[#4d8fff] font-bold text-sm">— Grove Club Condominium, Miami</p>
              </div>
            </div>
          </div>

          {/* Urgency */}
          <div
            ref={urgencyRef}
            className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-12 text-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            <h2 className="text-xl md:text-3xl font-black text-white mb-3">
              Stop Repeating Repairs. Fix the Problem at Its Source.
            </h2>
            <p className="text-gray-400 text-sm md:text-base mb-8">
              It&apos;s time for an engineered assessment — not another temporary fix.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto mb-10">
              {urgencyItems.map((item) => (
                <div key={item} className="flex items-center gap-3 text-left">
                  <span className="w-5 h-5 flex items-center justify-center shrink-0 text-[#0055ff]">
                    <i className="ri-checkbox-circle-fill"></i>
                  </span>
                  <span className="text-gray-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={onCTA}
                className="bg-[#0055ff] hover:bg-[#0044cc] text-white font-bold px-6 py-4 rounded-md text-sm uppercase tracking-wider transition-all duration-200 whitespace-normal sm:whitespace-nowrap w-full sm:w-auto"
              >
                Schedule a Free Engineering Inspection
              </button>
              <button
                onClick={onCTA}
                className="bg-transparent border-2 border-white/30 hover:border-white text-white font-bold px-6 py-4 rounded-md text-sm uppercase tracking-wider transition-all duration-200 whitespace-normal sm:whitespace-nowrap w-full sm:w-auto"
              >
                Request a Professional Water Intrusion Assessment
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
